import { get, isEmpty, isNil } from 'lodash';
import moment from 'moment';
import {
  ExpertReportType,
  TvictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import { VICTIM_EXPERTISE_FIELDS } from 'containers/data-structure';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import { Claim } from 'containers/PreOpeningPage/typings';
import {
  getVictimAge,
  getVictimKey,
  getVictimRevenu,
  hasDecision,
} from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { CURRENT_JUDICIARY_STEP_EXPERT_REPORT } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/constants';
import {
  mergeEvaluations,
  resolveExpertiseValue,
} from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { resolveSagisClaim } from 'helpers/utils';
import {
  STATUS_CONTESTED,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import {
  COMPTABLE_EXPERTISE_ID,
  MEDICAL_EXPERTISE_ID,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { getTheLatestEvaluationObject } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import { VictimType } from 'typings/globalTypes';

export const getFirstInstanceVictimExpertises = (
  appealVictimExpertises: TvictimExpertise[],
  firstInstanceVictimExpertises: TvictimExpertise[],
) => {
  const formattedFIVI = firstInstanceVictimExpertises.map((vi: any) => ({
    ...vi,
    isFirstInstance: true,
  }));
  const similarVictimExpertises: any[] = [];

  appealVictimExpertises.forEach((vi: any) => {
    formattedFIVI.forEach((veFI: any) => {
      if (
        String(vi.victimId) === String(veFI.victimId) &&
        isEmpty(similarVictimExpertises.find(item => item.id === veFI.id))
      ) {
        return similarVictimExpertises.push(veFI);
      }
    });
  });
  return similarVictimExpertises;
};
export const addEvaluationsToExpertReportAppealPayload = (
  expertReportProps: any,
  expertiseRaportFirstInstance: any,
  expertReport: { expertReport: ExpertReportType },
  victims: VictimType[],
  claim: Claim,
  policyInfo: any,
  dahirAmount: number,
) => {
  const RiskKey = get(resolveSagisClaim(policyInfo), 'offeredRisks', []).find(
    (element: { riskName: string }) => element.riskName === rcCoporeal,
  )?.riskId;
  const firstInstanceVictimExpertises = getFirstInstanceVictimExpertises(
    get(expertReport, 'expertReport.victimExpertises', []),
    get(expertiseRaportFirstInstance, 'victimExpertises', []),
  );
  const sagisEvaluations = [
    ...get(expertReport, 'expertReport.victimExpertises', []),
    ...firstInstanceVictimExpertises,
  ]
    .filter(
      vicExp =>
        !vicExp.isOld &&
        !vicExp.isRejectedJudgement &&
        (vicExp.status === STATUS_VALIDATED ||
          vicExp.status === STATUS_CONTESTED),
    )
    .map(vicExp => {
      const lastAccountingExpertise =
        get(expertReportProps, 'victimExpertises', []).find(
          (item: any) =>
            item?.victimId === vicExp?.victimId &&
            !item.isOld &&
            (item.status === STATUS_VALIDATED ||
              item.status === STATUS_CONTESTED) &&
            !item.isRejectedJudgement &&
            String(item.expertiseId) === COMPTABLE_EXPERTISE_ID,
        ) || {};
      const lastMedicalExpertise =
        get(expertReportProps, 'victimExpertises', []).find(
          (item: any) =>
            item?.victimId === vicExp?.victimId &&
            !item.isOld &&
            (item.status === STATUS_VALIDATED ||
              item.status === STATUS_CONTESTED) &&
            !item.isRejectedJudgement &&
            String(item.expertiseId) === MEDICAL_EXPERTISE_ID,
        ) || {};
      const lastEvaluation = getTheLatestEvaluationObject(
        getVictimKey(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims || [],
        ),
        dahirAmount,
      );

      return {
        gestionMode: '',
        materialAmount: '',
        currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
        judiciaryAmount: '',
        responsibilityRate:
          (vicExp.isDeathRelatedToClaim === null ||
            vicExp.isDeathRelatedToClaim === false) &&
          Number(vicExp.expertiseId) === 1 &&
          vicExp.itt === null
            ? 0
            : get(vicExp, 'sharedResponsability'),
        rateRecovery: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
          get(lastEvaluation, 'rateRecovery'),
        ),
        revenu:
          vicExp[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME] &&
          !vicExp?.isFirstInstance
            ? vicExp[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
            : isEmpty(lastAccountingExpertise) && vicExp?.isFirstInstance
            ? vicExp[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
            : !isEmpty(lastAccountingExpertise)
            ? lastAccountingExpertise[
                VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME
              ]
            : !isNil(
                getVictimRevenu(
                  vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                  victims || [],
                ),
              )
            ? getVictimRevenu(
                vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                victims || [],
              )
            : vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE],
        age: getVictimAge(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
          claim['lossDate'],
        ),
        mode: vicExp.injuryPostKey === '2' ? MODE.DECEASED : MODE.BLESSE,
        ratePretium: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
          get(lastEvaluation, 'ratePretium'),
        ),
        lossDate: moment(claim['lossDate']).toISOString(true),
        rateAestheticDamageWithoutConsequence: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
          ],
          get(lastEvaluation, 'rateAestheticDamageWithoutConsequence'),
        ),
        rateAestheticDamageWithConsequence: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE
          ],
          get(lastEvaluation, 'rateAestheticDamageWithConsequence'),
        ),
        rateCareerPrejudice: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
          get(lastEvaluation, 'rateCareerPrejudice'),
        ),
        rateSchoolingPrejudice: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
          get(lastEvaluation, 'rateSchoolingPrejudice'),
        ),
        rateIPP: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.IPP],
          lastMedicalExpertise,
          lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.IPP],
          get(lastEvaluation, 'rateIPP'),
        ),
        rateITT:
          Number(vicExp.expertiseId) === 2 && isEmpty(lastMedicalExpertise)
            ? lastEvaluation.rateITT
              ? lastEvaluation.rateITT
              : vicExp.previousItt
            : resolveExpertiseValue(
                vicExp,
                vicExp[VICTIM_EXPERTISE_FIELDS.ITT],
                lastMedicalExpertise,
                lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.ITT],
                get(lastEvaluation, 'rateITT'),
              ),
        victimKey: getVictimKey(
          vicExp[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims,
        ),
        medicalPharmaceuticalFees: resolveExpertiseValue(
          vicExp,
          vicExp[VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES],
          lastMedicalExpertise,
          lastMedicalExpertise[
            VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES
          ],
          get(lastEvaluation, 'medicalPharmaceuticalFees'),
        ),
        rateMedicalPharmaceuticalFees: '',
        transactionPhase: 1,
        JudiciartDecisionDate: get(vicExp, 'judgementDate', ''),
        expType: !get(vicExp, 'isFirstInstance', false)
          ? Number(vicExp.expertiseId)
          : Number(vicExp.expertiseId),
        isRecentExpertiseVictim: vicExp.isRecentExpertiseVictim,
        isFirstInstance: vicExp.isFirstInstance,
      };
    });
  const provisionBody = {
    riskID: RiskKey,
    date: moment().toISOString(true),
  };
  return {
    ...expertReport,
    sagisEvaluations: mergeEvaluations(sagisEvaluations),
    provisionBody: hasDecision(expertReport) ? provisionBody : {},
  };
};
